<script lang="ts">
  import { onMount } from 'svelte';
  import TriggerButton from './TriggerButton.svelte';
  import PanelHeader from './PanelHeader.svelte';
  import ProfileSection from './ProfileSection.svelte';
  import FeatureGrid from './FeatureGrid.svelte';
  import PanelFooter from './PanelFooter.svelte';
  import { getSupportedLang, isRtlLang, setCurrentWidgetLang, LANGUAGE_OPTIONS, t } from '../i18n/index';
  import { iconGlobe } from '../icons/index';
  import type { A11yPlusConfig, A11yProfile } from '../types';
  import { createTextTransformService } from '../services/text-transform';
  import { PROFILES } from '../profiles';
  import { isPreviewContext } from '../preview-url';

  let { config, shadow }: { config: A11yPlusConfig; shadow: ShadowRoot } = $props();

  // ─── State ───
  let isOpen = $state(false);
  let activeFeatures = $state<Map<string, boolean>>(new Map());
  let currentTheme = $state<'light' | 'dark'>('light');
  let panelEl: HTMLElement | null = $state(null);
  let triggerEl: HTMLElement | null = $state(null);
  let grabEl: HTMLElement | null = $state(null);
  let settingsOpen = $state(false);
  let themeMode = $state<'auto' | 'light' | 'dark'>('auto');
  let announcement = $state('');
  let widgetLang = $state('en');
  let activeProfileId = $state<string | null>(null);
  let langDropdownOpen = $state(false);
  // R8-5 / R11-3: counter bumped each time a lazy-loaded locale arrives
  // from the translate-proxy. Read inside the panel markup (data attr) so
  // Svelte re-runs every `t()` call when the locale set changes. The
  // counter has no other purpose.
  let localeVersion = $state(0);

  // Horizontal panel position (null = use CSS default from config)
  let panelX = $state<number | null>(null);
  const PANEL_X_KEY = 'accessify-panel-x';

  /** Estimate panel width for clamping when the element is not measurable yet
   *  (panel is display:none while closed, so offsetWidth == 0). Falls back to
   *  the CSS default (420px) or the configured override. */
  function estimatedPanelWidth(): number {
    if (panelEl && panelEl.offsetWidth > 0) return panelEl.offsetWidth;
    const configured = (config as any).panelWidth;
    const fromConfig = typeof configured === 'number' && configured > 0 ? configured : 420;
    // Never wider than the viewport (CSS clamps with min(panelW, 100vw)).
    return Math.min(fromConfig, window.innerWidth);
  }

  /** Clamp panelX into [0, innerWidth - panelWidth]. Re-writes the inline
   *  left and updates the persisted value. No-op when panelX is null
   *  (i.e. the CSS default anchor is still in effect). */
  function clampPanelPosition(): void {
    if (panelX === null) return;
    const panelW = estimatedPanelWidth();
    const maxLeft = Math.max(0, window.innerWidth - panelW);
    const clamped = Math.max(0, Math.min(maxLeft, panelX));
    if (clamped !== panelX) {
      panelX = clamped;
      if (!isPreviewContext()) { try { localStorage.setItem(PANEL_X_KEY, String(clamped)); } catch {} }
    }
  }

  const textTransformService = createTextTransformService();
  const STORAGE_KEY = 'accessify-prefs';

  // ─── Schema version for localStorage invalidation / migration ───
  // Bump this integer whenever a new widget release changes the MEANING
  // of any persisted key in a way that an old value could break the new
  // widget. Add a corresponding entry to MIGRATIONS below. Each migration
  // runs once, mutates localStorage, and advances the stored schema
  // number. Safe for opted-in keys only; unknown keys are left untouched.
  const SCHEMA_VERSION = 2;
  const SCHEMA_KEY = 'accessify-schema-version';

  interface SchemaMigration {
    from: number;
    to: number;
    run: () => void;
  }

  const MIGRATIONS: SchemaMigration[] = [
    // R11-6: legacy per-feature keys (e.g. `accessify-link-highlight`)
    // duplicated info that is already in `accessify-prefs`. Merge the
    // legacy values back into prefs (last-write-wins, prefs primary) and
    // delete the duplicates so we can never go stale again.
    {
      from: 1,
      to: 2,
      run: () => {
        const legacyKeys = [
          'accessify-link-highlight',
          'accessify-big-cursor',
          'accessify-hide-images',
          'accessify-animation-stop',
          'accessify-keyboard-nav',
          'accessify-reading-mask',
          'accessify-page-structure',
          'accessify-tts',
          'accessify-alt-text',
        ];
        let prefs: Record<string, unknown> = {};
        try { prefs = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}'); }
        catch { prefs = {}; }
        let touched = false;
        for (const key of legacyKeys) {
          const raw = localStorage.getItem(key);
          if (raw === null) continue;
          const feature = key.replace(/^accessify-/, '');
          if (!(feature in prefs)) prefs[feature] = raw === 'true' ? true : raw === 'false' ? false : raw;
          localStorage.removeItem(key);
          touched = true;
        }
        if (touched) localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
      },
    },
  ];

  function runSchemaMigrations(): void {
    try {
      let stored = parseInt(localStorage.getItem(SCHEMA_KEY) || '0', 10);
      if (!Number.isFinite(stored) || stored < 0) stored = 0;
      // Nothing stored yet = first ever mount. Pretend we're at the
      // current version; nothing to migrate forward.
      if (stored === 0 && localStorage.getItem(SCHEMA_KEY) === null) {
        localStorage.setItem(SCHEMA_KEY, String(SCHEMA_VERSION));
        return;
      }
      if (stored === SCHEMA_VERSION) return;
      // Run applicable migrations in order. Stop if no chain is available.
      while (stored < SCHEMA_VERSION) {
        const mig = MIGRATIONS.find(m => m.from === stored);
        if (!mig) break;
        try { mig.run(); } catch { /* best-effort per migration */ }
        stored = mig.to;
      }
      localStorage.setItem(SCHEMA_KEY, String(SCHEMA_VERSION));
    } catch { /* localStorage unavailable — ignore */ }
  }

  let activeCount = $derived(
    Array.from(activeFeatures.values()).filter(Boolean).length
  );

  let currentLangOption = $derived(
    LANGUAGE_OPTIONS.find(o => o.code === widgetLang)
  );

  function announce(message: string): void {
    announcement = '';
    requestAnimationFrame(() => { announcement = message; });
  }

  // ─── Persistence ───
  function loadPrefs(): void {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const prefs: Record<string, boolean> = JSON.parse(saved);
        activeFeatures = new Map(Object.entries(prefs));
      }
      // Im Dashboard-Preview die gespeicherte Drag-Position NICHT wiederherstellen
      // — der Owner soll die konfigurierte Default-Position (angedockt) sehen, nicht
      // eine frühere Besucher-Verschiebung, die sonst „mittig rumschwebt".
      if (!isPreviewContext()) {
        const savedX = localStorage.getItem(PANEL_X_KEY);
        if (savedX !== null) {
          const n = parseFloat(savedX);
          if (Number.isFinite(n) && n >= 0) {
            // „Absichern": eine gespeicherte Drag-Position wird beim Laden an
            // die NÄCHSTE Kante gedockt, nicht exakt wiederhergestellt. So kann
            // das Panel nie mitten im Bild kleben (z. B. wenn ein Besucher es
            // einmal in die Mitte gezogen hat) — beim nächsten Aufruf sitzt es
            // wieder sauber am Rand. Frei ziehen bleibt innerhalb der Sitzung
            // möglich (handlePanelDrag ist unverändert), nur das PERSISTIERTE
            // Ergebnis dockt an. Näher rechts → sauberer Default-Anker
            // (right:20px, panelX=null); näher links → linke Kante.
            const maxLeft = Math.max(0, window.innerWidth - estimatedPanelWidth());
            if (n >= maxLeft / 2) {
              panelX = null;
              try { localStorage.removeItem(PANEL_X_KEY); } catch {}
            } else {
              panelX = 0;
              try { localStorage.setItem(PANEL_X_KEY, '0'); } catch {}
            }
          } else {
            // Korrupter Wert (NaN/Infinity/negativ) → Default.
            panelX = null;
            try { localStorage.removeItem(PANEL_X_KEY); } catch {}
          }
        }
      }
    } catch { /* silently ignore */ }
    clampPanelPosition();
  }

  function savePrefs(): void {
    try {
      const obj = Object.fromEntries(activeFeatures);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(obj));
    } catch { /* silently ignore */ }
  }

  // ─── Theme ───
  function detectTheme(): void {
    if (config.theme === 'light' || config.theme === 'dark') {
      currentTheme = config.theme;
      return;
    }
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    currentTheme = mq.matches ? 'dark' : 'light';
    mq.addEventListener('change', (e: MediaQueryListEvent) => {
      if (config.theme === 'auto') {
        currentTheme = e.matches ? 'dark' : 'light';
        applyTheme();
      }
    });
  }

  function applyTheme(): void {
    // Always reflect the RESOLVED theme as data-theme. For theme="auto"
    // currentTheme is already resolved (matchMedia) by detectTheme + its
    // change listener. The #accessify-mount[data-theme="dark"] rule in
    // widget-styles applies the dark tokens with id-level specificity, so
    // dark mode now actually takes effect (the prefers-color-scheme :where()
    // block alone never beat the base #accessify-mount tokens).
    const mount = shadow.getElementById('accessify-mount');
    if (mount) mount.setAttribute('data-theme', currentTheme);
  }

  /** Resolve the widget's OWN appearance (Auto/Hell/Dunkel). Themes ONLY the
   *  widget chrome — never the host page. */
  function resolveAppearance(): void {
    currentTheme = themeMode === 'auto'
      ? (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
      : themeMode;
    applyTheme();
  }

  /** User-facing widget appearance switch (Auto / Hell / Dunkel), persisted.
   *  Widget-only — does NOT darken the website. */
  function setTheme(mode: 'auto' | 'light' | 'dark'): void {
    themeMode = mode;
    config.theme = mode;
    try { localStorage.setItem('accessify-theme', mode); } catch { /* ignore */ }
    resolveAppearance();
  }

  function applyLanguage(): void {
    const mount = shadow.getElementById('accessify-mount');
    const dir = isRtlLang(widgetLang) ? 'rtl' : 'ltr';
    if (mount) {
      mount.setAttribute('lang', widgetLang);
      mount.setAttribute('dir', dir);
    }
    shadow.host.setAttribute('lang', widgetLang);
    shadow.host.setAttribute('dir', dir);
    setCurrentWidgetLang(widgetLang);
  }

  // ─── Open / Close ───
  export function open(): void {
    // Re-validate panel position against the CURRENT viewport before showing.
    // The window may have been resized (or the saved position comes from a
    // wider window) since the panel was last visible. Without this check the
    // panel can render completely off-screen and the user sees nothing.
    clampPanelPosition();

    isOpen = true;
    requestAnimationFrame(() => {
      // After the panel is actually measurable, clamp once more using the
      // real offsetWidth instead of the estimate.
      clampPanelPosition();
      if (panelEl) {
        const first = panelEl.querySelector<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        first?.focus();
      }
    });
  }

  export function close(): void {
    isOpen = false;
    langDropdownOpen = false;
    settingsOpen = false;
    requestAnimationFrame(() => {
      const trigger = shadow.querySelector<HTMLElement>('.accessify-trigger');
      trigger?.focus();
    });
  }

  function toggleOpen(): void {
    if (isOpen) {
      close();
    } else {
      open();
    }
  }

  // ─── Hide widget for this visitor (EyeAble-style self-dismiss) ───
  // Close the panel, then ask the host wrapper (index.ts) to hide the whole
  // widget and persist the choice. The host then shows a toast explaining the
  // only way back: clearing this site's browser data (no Alt+0 anymore).
  function handleHide(): void {
    close();
    window.dispatchEvent(new CustomEvent('accessify:hide'));
  }

  // ─── Language (unified: widget UI + page translation) ───
  function handleLangChange(newLang: string): void {
    widgetLang = getSupportedLang(newLang);
    config.lang = widgetLang;
    langDropdownOpen = false;
    applyLanguage();

    const pageLang = document.documentElement.lang?.split('-')[0] || 'en';
    if (widgetLang !== pageLang) {
      textTransformService.translate(widgetLang);
    } else {
      textTransformService.restore();
    }
  }

  // ─── Panel Drag ───
  // Beim Loslassen nahe einer Kante dorthin schnappen: das Panel wird bündig und
  // die kanten-zugewandte Seite wieder eckig (wie im angedockten Default, Radius
  // via panelDockRadius). Nur frei in der Mitte bleibt es rundum gerundet.
  const PANEL_SNAP = 32;
  function handlePanelDrag(x: number): void {
    const maxLeft = Math.max(0, window.innerWidth - estimatedPanelWidth());
    const snapped = x <= PANEL_SNAP ? 0 : x >= maxLeft - PANEL_SNAP ? maxLeft : x;
    panelX = snapped;
    if (!isPreviewContext()) { try { localStorage.setItem(PANEL_X_KEY, String(snapped)); } catch {} }
  }

  // ─── Feature toggling ───
  function handleFeatureToggle(id: string, enabled: boolean): void {
    activeFeatures.set(id, enabled);
    activeFeatures = new Map(activeFeatures);

    // If an active profile owned this feature, the profile is no longer
    // fully active — drop the activeProfileId so the accordion no longer
    // shows the checkmark. This is R6-8's "sub-toggle deactivation resolves
    // profile state" requirement.
    if (activeProfileId !== null) {
      const profile = PROFILES.find((p) => p.id === activeProfileId);
      if (profile && profile.features.includes(id)) {
        activeProfileId = null;
      }
    }

    savePrefs();

    const featureKey = `feature.${id.replace(/-([a-z])/g, (_, c) => c.toUpperCase())}`;
    const name = t(featureKey, widgetLang) || id;
    const action = enabled ? t('status.activated', widgetLang) : t('status.deactivated', widgetLang);
    announce(`${name} ${action}`);
  }

  function handleTextSize(_size: number): void {
    // Text size only affects the page, not the widget
  }

  function resetAll(): void {
    activeFeatures = new Map();
    activeProfileId = null;
    savePrefs();
    textTransformService.restore();
    window.dispatchEvent(new CustomEvent('accessify:reset'));
    announce(t('widget.resetConfirm', widgetLang));
  }

  function handleProfileActivate(profile: A11yProfile): void {
    if (activeProfileId === profile.id) {
      activeProfileId = null;
      for (const fid of profile.features) {
        activeFeatures.set(fid, false);
      }
      activeFeatures = new Map(activeFeatures);
      savePrefs();
      window.dispatchEvent(new CustomEvent('accessify:reset'));
      announce(`${t(profile.nameKey, widgetLang)} ${t('status.deactivated', widgetLang)}`);
      return;
    }
    activeProfileId = profile.id;
    for (const fid of profile.features) {
      activeFeatures.set(fid, true);
    }
    activeFeatures = new Map(activeFeatures);
    savePrefs();
    announce(`${t(profile.nameKey, widgetLang)} ${t('status.activated', widgetLang)}`);
  }

  // ─── Keyboard ───
  function handleKeydown(e: KeyboardEvent): void {
    if (e.key === 'Escape') {
      if (langDropdownOpen) {
        langDropdownOpen = false;
        e.preventDefault();
        return;
      }
      if (isOpen) {
        e.preventDefault();
        close();
        return;
      }
    }
    if (e.key === 'Tab' && isOpen && panelEl) {
      const focusable = panelEl.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = shadow.activeElement || document.activeElement;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  // ─── Panel inline style for horizontal position ───
  // Sobald das Panel gedraggt ist (panelX gesetzt), floatet es frei von der
  // Kante → alle vier Ecken runden (angedockt ist nur die kanten-abgewandte
  // Seite gerundet, via Stylesheet `border-radius: 20px 0 0 20px`). Reaktiv hier,
  // weil dieser Style das imperative Drag-Feedback aus PanelHeader nach dem Drop
  // sonst wieder überschreiben würde.
  // Function declaration (hoisted) so the $derived below can call it
  // regardless of source order. On the mobile bottom-sheet the panel is
  // bottom-anchored full-width, so a persisted horizontal drag-position
  // (panelX, saved on a wider desktop viewport) must NOT be applied —
  // otherwise the inline `left` pushes the sheet off-screen. Returning ''
  // here lets the @media(max-width:640px) sheet rules take over cleanly.
  function isMobileViewport(): boolean {
    return typeof window !== 'undefined' && window.matchMedia('(max-width: 640px)').matches;
  }

  // Ecken passend zur Position: an einer Kante angedockt → die kanten-zugewandte
  // Seite ist eckig (wie der Stylesheet-Default), frei in der Mitte → alle vier
  // gerundet. So wird das Panel wieder „eckig", wenn es in die Ecke gezogen wird.
  let panelDockRadius = $derived.by(() => {
    if (panelX === null) return '20px';
    const maxLeft = Math.max(0, window.innerWidth - estimatedPanelWidth());
    if (panelX <= PANEL_SNAP) return '0 20px 20px 0';
    if (panelX >= maxLeft - PANEL_SNAP) return '20px 0 0 20px';
    return '20px';
  });

  let panelStyle = $derived(
    panelX !== null && !isMobileViewport()
      ? `left: ${panelX}px; right: auto; border-radius: ${panelDockRadius};`
      : ''
  );

  // ─── Mobile scroll lock ───
  // Greift nur bei Mobile-Breakpoint UND offenem Panel. Dieser State
  // muss BEIDE Dimensionen reaktiv tracken:
  //   - `isOpen`            — Svelte-Rune, wird automatisch getrackt
  //   - `mobileQuery.matches` — MediaQueryList, kein Svelte-State.
  //
  // Ohne die Reactivity auf matches würde ein Portrait→Landscape-Wechsel
  // bei offenem Panel `body.overflow='hidden'` stehen lassen, weil der
  // $effect nur auf isOpen-Änderungen re-evaluiert (Mobile→Desktop-
  // Layout-Flip, Host-Page bleibt scroll-locked).
  //
  // Fix: wir spiegeln `matches` in einen $state, der per
  // MediaQueryList.change-Event aktualisiert wird. Damit ist die
  // Abhängigkeit Svelte-sichtbar und der $effect cleanup'd beim
  // Wechsel zuverlässig.
  const mobileQuery = typeof window !== 'undefined'
    ? window.matchMedia('(max-width: 640px)')
    : null;
  let isMobileMatched = $state(mobileQuery?.matches ?? false);

  $effect(() => {
    if (!mobileQuery) return;
    const onChange = (e: MediaQueryListEvent) => { isMobileMatched = e.matches; };
    mobileQuery.addEventListener('change', onChange);
    return () => mobileQuery.removeEventListener('change', onChange);
  });

  $effect(() => {
    if (!isOpen || !isMobileMatched) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  });

  // ─── Viewport-resize clamping ───
  // If the user resizes the window (or the mobile visualViewport changes),
  // re-clamp the panel position so it never floats off-screen. Covers both
  // the classic window.resize event and the mobile virtual-keyboard /
  // pinch-zoom path via visualViewport.resize.
  $effect(() => {
    if (typeof window === 'undefined') return;
    const onResize = () => clampPanelPosition();
    window.addEventListener('resize', onResize);
    window.visualViewport?.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.visualViewport?.removeEventListener('resize', onResize);
    };
  });

  // ─── Mobile bottom-sheet: swipe the grab bar down to dismiss ───
  // Touch-events (not Pointer-events): on iOS the pointer-based version fought
  // the page's native scroll/overscroll and felt janky. Here we preventDefault
  // on downward touchmove (kills the scroll fight), drive the transform via
  // requestAnimationFrame (smooth), and dismiss on a 30%-of-height drag OR a
  // fast downward flick. Desktop never triggers this — the grab bar is
  // display:none above 640px.
  $effect(() => {
    const grab = grabEl;
    const panel = panelEl;
    if (!grab || !panel) return;
    let startY = 0;
    let dy = 0;
    let startT = 0;
    let dragging = false;
    let raf = 0;
    const apply = () => { raf = 0; panel.style.transform = `translateY(${dy}px)`; };
    const onStart = (e: TouchEvent) => {
      if (e.touches.length !== 1) return;
      startY = e.touches[0].clientY;
      dy = 0;
      startT = Date.now();
      dragging = true;
      panel.style.transition = 'none';
    };
    const onMove = (e: TouchEvent) => {
      if (!dragging) return;
      dy = Math.max(0, e.touches[0].clientY - startY);
      if (dy > 0) e.preventDefault();
      if (!raf) raf = requestAnimationFrame(apply);
    };
    const onEnd = () => {
      if (!dragging) return;
      dragging = false;
      if (raf) { cancelAnimationFrame(raf); raf = 0; }
      panel.style.transition = '';
      const velocity = dy / Math.max(1, Date.now() - startT); // px per ms
      const h = panel.getBoundingClientRect().height || 1;
      panel.style.transform = '';
      if (dy > h * 0.3 || velocity > 0.5) close();
    };
    grab.addEventListener('touchstart', onStart, { passive: true });
    grab.addEventListener('touchmove', onMove, { passive: false });
    grab.addEventListener('touchend', onEnd);
    grab.addEventListener('touchcancel', onEnd);
    return () => {
      grab.removeEventListener('touchstart', onStart);
      grab.removeEventListener('touchmove', onMove);
      grab.removeEventListener('touchend', onEnd);
      grab.removeEventListener('touchcancel', onEnd);
    };
  });

  // The page-contrast feature drives the widget's appearance to MATCH the page
  // (Hoher Kontrast → dark widget). Direction is page → widget ONLY; the
  // widget's own dark-mode never touches the page. Contrast off → restore the
  // user's chosen appearance.
  $effect(() => {
    const onContrastChanged = (e: Event) => {
      const mode = (e as CustomEvent).detail?.mode;
      if (mode === 'high' || mode === 'dark') { currentTheme = 'dark'; applyTheme(); }
      else if (mode === 'light') { currentTheme = 'light'; applyTheme(); }
      else { resolveAppearance(); }
    };
    window.addEventListener('accessify:contrast-changed', onContrastChanged);
    return () => window.removeEventListener('accessify:contrast-changed', onContrastChanged);
  });

  // ─── Lifecycle ───
  onMount(() => {
    widgetLang = getSupportedLang(config.lang || 'en');
    // Run migrations BEFORE loadPrefs so any schema-dependent values are
    // already in their correct post-migration shape when we read them.
    runSchemaMigrations();
    // Restore a persisted theme choice (settings menu) before resolving.
    try {
      const storedTheme = localStorage.getItem('accessify-theme');
      if (storedTheme === 'auto' || storedTheme === 'light' || storedTheme === 'dark') {
        config.theme = storedTheme;
      }
    } catch { /* ignore */ }
    themeMode = config.theme === 'light' || config.theme === 'dark' ? config.theme : 'auto';
    detectTheme();
    applyTheme();
    // If a page-contrast was active from a prior visit, the widget matches it:
    // Hoher Kontrast → dark widget, Heller Kontrast → light. (Page → widget.)
    try {
      const cm = localStorage.getItem('accessify-contrast-mode');
      if (cm === 'high' || cm === 'dark') { currentTheme = 'dark'; applyTheme(); }
      else if (cm === 'light') { currentTheme = 'light'; applyTheme(); }
    } catch { /* ignore */ }
    loadPrefs();
    applyLanguage();

    // R8-5 / R11-3: react when a lazy-loaded locale arrives from the
    // translate proxy. Re-resolve the widget language (was it the one we
    // were waiting on?) and bump the version counter so Svelte re-runs
    // every t() call in the markup.
    const onLocale = (e: Event) => {
      const detail = (e as CustomEvent).detail as { lang?: string } | undefined;
      if (!detail?.lang) return;
      widgetLang = getSupportedLang(config.lang || 'en');
      localeVersion++;
    };
    window.addEventListener('accessify:locale-loaded', onLocale);
    return () => window.removeEventListener('accessify:locale-loaded', onLocale);
  });
</script>

<svelte:window onkeydown={handleKeydown} />

<!-- R8-5: hidden marker that reads `localeVersion`. When loadLocale()
     finishes and we bump the counter, Svelte re-runs every t() call
     in the entire component because this attribute changes. -->
<span class="sr-only" data-accessify-locale-version={localeVersion} aria-hidden="true"></span>

<div
  class="sr-only"
  role="status"
  aria-live="polite"
  aria-atomic="true"
>
  {announcement}
</div>

<TriggerButton
  {isOpen}
  lang={widgetLang}
  onclick={toggleOpen}
  bind:el={triggerEl}
/>

<!-- Mobile-only scrim behind the bottom-sheet (display:none on desktop via
     widget-styles). Tap to dismiss; the accessible close paths remain ESC
     and the header close button. -->
<!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions -->
<div class="accessify-scrim" data-open={isOpen} onclick={close} aria-hidden="true"></div>

<div
  class="accessify-panel"
  role="dialog"
  aria-label={t('widget.title', widgetLang)}
  aria-hidden={!isOpen}
  dir={isRtlLang(widgetLang) ? 'rtl' : 'ltr'}
  style={panelStyle}
  bind:this={panelEl}
>
  <!-- Mobile bottom-sheet grab bar (display:none on desktop). Swipe it down
       to dismiss; the visible pill is drawn via ::before in widget-styles. -->
  <div class="accessify-grab" bind:this={grabEl} aria-hidden="true"></div>
  <PanelHeader
    lang={widgetLang}
    {activeCount}
    onclose={close}
    onsettings={() => { settingsOpen = true; }}
    ondrag={handlePanelDrag}
    {panelEl}
  />

  <div class="accessify-body">
    <!-- Sprache / Seitenübersetzung — bleibt VORNE im Hauptpanel. Übersetzen
         ist eine Kern-Funktion, kein verstecktes Setting. -->
    <div class="accessify-lang-dropdown">
      <button
        class="accessify-lang-toggle"
        aria-expanded={langDropdownOpen}
        aria-controls="accessify-lang-list"
        onclick={() => { langDropdownOpen = !langDropdownOpen; }}
      >
        <span class="accessify-lang-toggle-left">
          {@html iconGlobe()}
          <span>{currentLangOption?.flag || ''} {currentLangOption?.label || widgetLang}</span>
        </span>
        <svg class="accessify-lang-chevron" class:accessify-lang-chevron--open={langDropdownOpen} viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
      </button>
      {#if langDropdownOpen}
        <div id="accessify-lang-list" class="accessify-lang-list" role="listbox" aria-label={t('widget.title', widgetLang)}>
          {#each LANGUAGE_OPTIONS as option}
            <button
              class="accessify-lang-option"
              role="option"
              aria-selected={widgetLang === option.code}
              data-active={widgetLang === option.code}
              onclick={() => handleLangChange(option.code)}
            >
              <span>{option.flag || ''} {option.label}</span>
              {#if widgetLang === option.code}
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
              {/if}
            </button>
          {/each}
        </div>
      {/if}
    </div>

    <div class="accessify-section-divider"></div>

    <!-- Funktionen: Kontrast, Textgröße, Feature-Cards. Erscheinungsbild +
         „Alle zurücksetzen" liegen im Einstellungs-Overlay (Zahnrad). -->
    <FeatureGrid
      config={{ ...config, lang: widgetLang }}
      {activeFeatures}
      ontoggle={handleFeatureToggle}
      ontextsize={handleTextSize}
    />

    <!-- Herkunftszeile im Hauptpanel: das Widget ist kostenlos, und jede
         Installation ist die einzige Stelle, an der Besucher (darunter andere
         Website-Betreiber) erfahren, woher es kommt. Eine ruhige Textzeile am
         Ende des scrollbaren Inhalts, kein Banner. -->
    <div class="accessify-footer-brand">
      <a
        href={widgetLang === 'de' ? 'https://goaccessify.de/de?ref=widget' : 'https://goaccessify.de/en?ref=widget'}
        target="_blank"
        rel="noopener noreferrer"
      >{t('widget.poweredBy', widgetLang)}</a>
    </div>
  </div>

  {#if settingsOpen}
    <!-- Einstellungen ("Zahnrad") — überlagert das Panel. Bündelt die Widget-
         Vorlieben (Sprache, Erscheinungsbild) und unten gepinnt die Meta-
         Aktionen (Feedback geben · Widget ausblenden · Erklärung). -->
    <div class="accessify-settings" role="dialog" aria-label={t('ui.settings', widgetLang)}>
      <div class="accessify-settings-head">
        <span class="accessify-settings-title">{t('ui.settings', widgetLang)}</span>
        <button
          type="button"
          class="accessify-header-btn accessify-header-btn--icon accessify-header-btn--close"
          aria-label={t('widget.close', widgetLang)}
          onclick={() => { settingsOpen = false; }}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12"/></svg>
        </button>
      </div>
      <div class="accessify-settings-body">
        <!-- Erscheinungsbild (Hell/Dunkel des Widgets) — themt NUR das Widget-
             Panel, nie die Host-Seite (Seite→Widget läuft separat über „Stark"
             im Kontrast). -->
        <div class="accessify-control-row">
          <div class="accessify-control-label">
            <span class="accessify-control-label-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 3v18a9 9 0 0 0 0-18z" fill="currentColor" stroke="none"/></svg>
            </span>
            <span class="accessify-control-label-text">{t('ui.appearance', widgetLang)}</span>
          </div>
          <div class="accessify-chip-row" role="radiogroup" aria-label={t('ui.appearance', widgetLang)}>
            {#each [
              { id: 'auto',  label: t('ui.auto', widgetLang), icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 2v20"/><path d="M12 2a10 10 0 0 1 0 20" fill="currentColor"/></svg>' },
              { id: 'light', label: t('ui.light', widgetLang), icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>' },
              { id: 'dark',  label: t('ui.dark', widgetLang), icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>' },
            ] as mode}
              <button
                type="button"
                class="accessify-chip accessify-chip--icon"
                role="radio"
                aria-checked={themeMode === mode.id}
                data-active={themeMode === mode.id}
                onclick={() => setTheme(mode.id as 'auto' | 'light' | 'dark')}
              >
                <span class="accessify-chip-icon" aria-hidden="true">{@html mode.icon}</span>
                <span>{mode.label}</span>
              </button>
            {/each}
          </div>
        </div>

        <!-- Alle zurücksetzen — Standard-Einstellung, setzt Kontrast, Textgröße
             und alle Funktionen zurück (gleiche Aktion wie das Reset-Icon im
             Header, hier mit Label gut auffindbar). -->
        <button type="button" class="accessify-settings-action" onclick={resetAll}>
          <span class="accessify-settings-action-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 12a9 9 0 1 0 3-6.7L3 8"/><path d="M3 3v5h5"/></svg>
          </span>
          <span>{t('ui.resetAll', widgetLang)}</span>
        </button>
      </div>

      <!-- Meta-Aktionen unten gepinnt: Feedback · Ausblenden · Erklärung -->
      <PanelFooter
        lang={widgetLang}
        statementUrl={config.statementUrl}
        siteKey={(config as any).siteKey}
        apiBase={(config as any).proxyUrl}
        allowHide={(config as any).allowVisitorHide !== false}
        onhide={handleHide}
      />
    </div>
  {/if}
</div>
